"use client"

import { Loading } from "@/shared/components/Loading"
import {
  IProduct,
  PRODUCT_TYPES,
} from "@repo/loomlock/model-types"
import React, { useState } from "react"
import { CreateListingModal } from "./CreateListingModal"
import { useNFTsGrid } from "./hooks/useNFTsGrid"
import { NFTCard } from "./NFTCard"

export const NFTsGrid: React.FC = () => {
  const [individualListingNFT, setIndividualListingNFT] = useState<
    IProduct | null
  >(null)
  const [showIndividualListingModal, setShowIndividualListingModal] =
    useState(false)

  const {
    filterType,
    setFilterType,
    sortType,
    setSortType,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    paginatedNFTs,
    totalNFTs,
    isLoading,
    hasNFTs,
  } = useNFTsGrid()

  const handleListNFT = (nft: IProduct) => {
    setIndividualListingNFT(nft)
    setShowIndividualListingModal(true)
  }

  const handleCloseIndividualListing = () => {
    setShowIndividualListingModal(false)
    setIndividualListingNFT(null)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    )
  }

  if (!hasNFTs) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center text-white">
          <div className="text-6xl mb-4">📦</div>
          <p className="text-lg">No NFTs found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="flex flex-wrap gap-4 items-center justify-between bg-black/20 p-4 rounded-lg">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">Filter:</span>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-32 bg-black/50 text-white border border-white/20 rounded px-3 py-1.5"
            >
              <option value="all">All</option>
              {PRODUCT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-white font-medium">Sort:</span>
            <select
              value={sortType}
              onChange={(e) => setSortType(e.target.value as any)}
              className="w-40 bg-black/50 text-white border border-white/20 rounded px-3 py-1.5"
            >
              <option value="newest">Newest First</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[200px] bg-black/50 text-white border border-white/20 rounded px-3 py-1.5 placeholder:text-white/50"
          />
        </div>
      </div>

      {/* Results Count */}
      <div className="text-white">
        Showing {paginatedNFTs.length} of {totalNFTs} NFTs
      </div>

      {/* NFT Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {paginatedNFTs.map(({ nft, type }) => (
          <NFTCard
            key={`${type}-${nft.tokenId}`}
            nft={nft}
            type={type}
            onList={handleListNFT}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalNFTs > pageSize && (
        <div className="flex justify-center items-center gap-4 text-white">
          <button
            type="button"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-black/50 border border-white/20 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {Math.ceil(totalNFTs / pageSize)}
          </span>
          <button
            type="button"
            onClick={() =>
              setCurrentPage(
                Math.min(Math.ceil(totalNFTs / pageSize), currentPage + 1),
              )
            }
            disabled={currentPage >= Math.ceil(totalNFTs / pageSize)}
            className="px-4 py-2 bg-black/50 border border-white/20 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
          >
            Next
          </button>
        </div>
      )}

      {/* Empty State for Filtered Results */}
      {totalNFTs === 0 && (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center text-white">
            <div className="text-6xl mb-4">🔍</div>
            <p className="text-lg">No NFTs match your filters</p>
          </div>
        </div>
      )}

      {/* Individual Listing Modal */}
      <CreateListingModal
        open={showIndividualListingModal}
        onClose={handleCloseIndividualListing}
        onSuccess={() => {
          // Refresh logic could be added here if needed
          handleCloseIndividualListing()
        }}
        nft={individualListingNFT || undefined}
      />
    </div>
  )
}
